/** True when the amount actually carries paise (₹1,192.50 does; ₹1,192.00 does not). */
export const hasPaise = (v: any): boolean => Math.round(Number(v ?? 0) * 100) % 100 !== 0;

/**
 * THE money formatter for exact amounts — payables, ledger rows, line totals, dialogs.
 *
 * Paise are shown ONLY when the amount has them, so a whole rupee figure reads as "₹1,192"
 * instead of the noisy "₹1,192.00", while "₹1,192.50" keeps both digits (never "₹1,192.5" —
 * half a paise-pair looks like a typo on money).
 *
 *   1192      → "₹1,192"
 *   1192.5    → "₹1,192.50"
 *   1192.05   → "₹1,192.05"
 *
 * Use this wherever the exact amount matters. For rounded KPI tiles and dense list columns use
 * `inr` below (now the same rule) or `inrShort` for lakh/crore abbreviation.
 */
export const inr2 = (v: any): string => {
  const n = Number(v ?? 0);
  return rupee(n, Math.abs(n).toLocaleString('en-IN', { minimumFractionDigits: hasPaise(n) ? 2 : 0, maximumFractionDigits: 2 }));
};

/**
 * The sign goes BEFORE the ₹: "-₹0.48", never "₹-0.48". Callers pass the already-formatted
 * magnitude; a figure that rounds to zero ("0", "0.00") carries no sign — "-₹0" is noise.
 */
export const rupee = (n: number, magnitude: string): string =>
  `${n < 0 && /[1-9]/.test(magnitude) ? '-' : ''}₹${magnitude}`;

/**
 * Money for KPI tiles, chart labels and dense columns. Owner rule (2026-11-03): money never
 * hides paise — an amount that has paise shows both digits ("₹16.20"), a whole rupee stays
 * whole ("₹3,000"). So this is now identical to `inr2`; the name is kept for its ~300 callers.
 */
export const inr = (v: any): string => inr2(v);

/**
 * Compact ₹ in INDIAN units — 85k / 1.2L / 3Cr. For a chart label or a tile that has no room for
 * "₹78,00,864"; the exact figure belongs in the tooltip or the table beside it. Lossy by design.
 */
export const inrShort = (v: any): string => {
  const n = Number(v) || 0, a = Math.abs(n);
  if (a >= 1e7) return rupee(n, `${(a / 1e7).toLocaleString('en-IN', { maximumFractionDigits: 1 })}Cr`);
  if (a >= 1e5) return rupee(n, `${(a / 1e5).toLocaleString('en-IN', { maximumFractionDigits: 1 })}L`);
  if (a >= 1e3) return rupee(n, `${Math.round(a / 1e3)}k`);
  // A full (unabbreviated) amount follows the money rule; only the k / L / Cr units round.
  return inr2(n);
};

/**
 * Format a quantity / unit value for display: thousands-separated, up to 3
 * decimals, but WITHOUT forced trailing zeros — so 5 → "5", 2.5 → "2.5",
 * 1.25 → "1.25".
 *
 * Quantities can be fractional UOM (e.g. 1.5 L, 0.25 kg), so unlike currency we
 * never round to whole numbers — that would misreport stock.
 */
export const fmtQty = (v: any) =>
  Number(v ?? 0).toLocaleString('en-IN', { maximumFractionDigits: 3 });

/**
 * Human-friendly UOM display for a MEASURE (a pack size / volume / weight).
 * Sub-unit-aware so a fractional quantity reads naturally instead of as a decimal:
 *
 *   0.25 LTR → "250 ml"   0.5 KG → "500 g"   1 LTR → "1 L"   25 KG → "25 kg"
 *
 * Millilitre / gram inputs roll the other way once they reach a whole base unit
 * (1500 g → "1.5 kg", 2000 ml → "2 L"). Count / unknown UOMs (NOS, BAG, BOX and
 * any custom master code) are left as-is: "<qty> <UOM>".
 *
 * NOTE: only use this for an actual measured quantity (pack_size). Stock / order
 * quantities are a COUNT OF PACKS, not a volume — keep those on fmtQty/fmtPackQty.
 */
export function fmtUom(value: any, uom?: string): string {
  const v = Number(value);
  const code = (uom ?? '').trim();
  if (!Number.isFinite(v)) return code ? `${value} ${code}` : String(value ?? '').trim();
  const U = code.toUpperCase();
  const abs = Math.abs(v);

  // Volume — litres ⇄ millilitres
  if (U === 'LTR' || U === 'L' || U === 'LITRE' || U === 'LITER') {
    return v !== 0 && abs < 1 ? `${fmtQty(v * 1000)} ml` : `${fmtQty(v)} L`;
  }
  if (U === 'ML') {
    return abs >= 1000 ? `${fmtQty(v / 1000)} L` : `${fmtQty(v)} ml`;
  }
  // Weight — kilograms ⇄ grams
  if (U === 'KG' || U === 'KGS' || U === 'KILOGRAM' || U === 'KILOGRAMME') {
    return v !== 0 && abs < 1 ? `${fmtQty(v * 1000)} g` : `${fmtQty(v)} kg`;
  }
  if (U === 'G' || U === 'GM' || U === 'GMS' || U === 'GRAM' || U === 'GRAMME') {
    return abs >= 1000 ? `${fmtQty(v / 1000)} kg` : `${fmtQty(v)} g`;
  }
  // Counts / unknown UOM — show the number with its code unchanged.
  return code ? `${fmtQty(v)} ${code}` : fmtQty(v);
}

/**
 * Canonical variant label — the SAME logic the products catalog uses, so a variant reads identically
 * everywhere (orders, approve dialog, catalog): derive the pack size from `packSize`+`uom` ("500 ml")
 * when a pack size is set, else fall back to the stored `variantLabel` (or the bare UOM). This avoids
 * an inconsistency where a hand-typed `variantLabel` ("0.5L") disagrees with the derived pack ("500 ml").
 */
export function variantText(packSize: any, uom?: string, fallbackLabel?: string | null): string {
  return Number(packSize) > 0 ? fmtUom(packSize, uom) : (fallbackLabel || (uom ?? '') || '');
}

/**
 * True when a SKU is just a re-spelling of a product label already on screen — so callers can avoid
 * printing the name twice ("AGN 500 ml · AGN 500ml"). Normalisation is UNIT-AWARE, so "0.5L" and
 * "500ml" (and "1L" vs "1000ml", "2kg" vs "2000g") collapse to the same token. Pass every visible label.
 */
export function isRedundantSku(sku?: string | null, ...labels: (string | null | undefined)[]): boolean {
  if (!sku) return true;
  const n = normalizeMeasureLabel(sku);
  return !!n && labels.some((l) => l && normalizeMeasureLabel(l) === n);
}

/**
 * The one or two lines to print for a product that has both a NAME and a SKU.
 *
 * `isRedundantSku` only catches an exact re-spelling, which left the commonest case on screen: a SKU
 * that carries the name PLUS the pack ("AGN" above "AGN 1L", "AGN" above "AGN 500ml"). Two lines, and
 * the first one is a prefix of the second — so the row says the product's name twice and the reader
 * has to get to line two before learning which pack it is. When one label contains the other, the
 * LONGER one is the whole truth and is printed alone; only a SKU that genuinely says something new
 * ("GreenGro Bio" / "AGN-1L") earns a second line.
 *
 * Containment is tested on the unit-normalised token, so "AGN 0.5L" is recognised as carrying "AGN"
 * whichever spelling of the size either side happens to use.
 */
export function productLabel(name?: string | null, sku?: string | null): { primary: string; secondary: string | null } {
  const nm = String(name ?? '').trim();
  const sk = String(sku ?? '').trim();
  if (!sk) return { primary: nm, secondary: null };
  if (!nm) return { primary: sk, secondary: null };
  const a = normalizeMeasureLabel(nm);
  const b = normalizeMeasureLabel(sk);
  if (!a || !b) return { primary: nm, secondary: sk };
  // The SKU spells the name and then some → it IS the label. The reverse (a name that already
  // contains its own SKU) is the same situation seen from the other side.
  if (b.startsWith(a)) return { primary: sk, secondary: null };
  if (a.startsWith(b)) return { primary: nm, secondary: null };
  return { primary: nm, secondary: sk };
}

/**
 * A batch number with the product stripped off its front: printed beside the label "AGN 500ml", the
 * batch "AGN 500ml-OPENING-377" reads as "OPENING-377".
 *
 * Batch numbers are minted FROM the SKU, so a row that prints the product and then its batch says
 * the same word twice — three times where the product name is also a prefix of its SKU ("AGN", "AGN
 * 500ml", "AGN 500ml-OPENING-377" stacked in one cell). Only the DISPLAY is shortened: hand the full
 * number to a title / CopyValue so nothing becomes uncopyable. Pass every label the cell already
 * shows; the longest match wins, and if stripping would leave nothing recognisable (under two
 * characters) the whole number is kept.
 */
export function batchSuffix(batchNo?: string | null, ...labels: (string | null | undefined)[]): string {
  const b = String(batchNo ?? '').trim();
  if (!b) return '';
  const lower = b.toLowerCase();
  let best = '';
  for (const l of labels) {
    const t = String(l ?? '').trim();
    if (t.length > best.length && lower.startsWith(t.toLowerCase())) best = t;
  }
  if (!best) return b;
  // Drop whatever separator the mint left between the two halves ("-", "/", "_", "·", spaces).
  const rest = b.slice(best.length).replace(/^[\s\-/_·]+/, '');
  return rest.length >= 2 ? rest : b;
}

/**
 * Reduce a label to a comparable token, converting measures to a single base unit so that different
 * spellings of the same size collapse: "0.5L" / "500ml" → `500ml`, "1 Litre" / "1L" → `1000ml`,
 * "25 Kg" → `25000g`. Everything that isn't a letter or digit is dropped.
 *
 * Exported because "does this text already say the size?" is asked in more than one place — by
 * `isRedundantSku` above, and by callers deciding whether to append a derived pack to a product name
 * that already carries it ("Bioshakti 25 Kg" must not render as "Bioshakti 25 Kg 25 kg"). Comparing
 * the RAW strings can't answer it: the two spellings never match literally.
 */
export function normalizeMeasureLabel(s?: string | null): string {
  return String(s ?? '')
    .toLowerCase()
    .replace(/(\d*\.?\d+)\s*(?:l|ltr|litre|liter|litres|liters)\b/g, (_m, n) => `${Math.round(parseFloat(n) * 1000)}ml`)
    .replace(/(\d*\.?\d+)\s*(?:kg|kilogram|kilograms|kgs)\b/g, (_m, n) => `${Math.round(parseFloat(n) * 1000)}g`)
    .replace(/[^a-z0-9]/g, '');
}

/**
 * Pack-aware quantity label. Across the whole system a stock / order quantity is a
 * COUNT OF PACKS (SKU units), e.g. "Biovin 20Kg" qty 15 = 15 bags of 20 kg, and
 * "AGN 1L" qty 11 = 11 bottles of 1 L. `packSize` is how much one pack holds in `uom`.
 *
 * ALWAYS shown as "count × pack" so the number reads as a pack count, never as a
 * weight/volume — consistent regardless of pack size:
 *   15 × 20 KG · 9 × 10 KG · 11 × 1 LTR · 5 × 2 LTR
 * (falls back to the bare count when no UOM is known).
 */
export function fmtPackQty(qty: any, packSize?: any, uom?: string): string {
  const nq = fmtQty(qty);
  const u = (uom || '').trim();
  if (!u) return nq;
  const ps = Number(packSize) || 1;
  // The pack portion is a measure → render it UOM-friendly (e.g. 0.5 LTR → "500 ml").
  return `${nq} × ${fmtUom(ps, u)}`;
}

/** The pack count with a "pack(s)" word — use where a separate column already shows the pack size. */
export function fmtPacks(qty: any): string {
  const nq = fmtQty(qty);
  return `${nq} ${Number(qty) === 1 ? 'pack' : 'packs'}`;
}

/**
 * Count + noun, pluralised properly: `plural(1, 'day')` → "1 day", `plural(33, 'day')` → "33 days".
 *
 * "(s)" is the author declining to decide in a place where the number already decides — it was
 * asked about twice on the approvals hub ("1 line(s) · 20 unit(s)"). `many` covers nouns English
 * does not pluralise with a bare "s": `plural(2, 'entry', 'entries')`.
 */
export const plural = (n: number, one: string, many = `${one}s`): string =>
  `${n} ${Math.abs(n) === 1 ? one : many}`;
